import type { ReactNode } from 'react';

export type PolicySection = {
  id: string;
  title: string;
  content: ReactNode;
};

const operatorRows = [
  ['상호', '할리'],
  ['대표자', '김남기'],
  ['사업자등록번호', '776-34-01509'],
  ['사업장 주소', '충남 서산시 한서1로 46, 한서대학교 인곡관 1층 중장년기술창업센터'],
  ['개인정보 보호 및 고객지원', 'info@agitwidget.com / 010-9037-1943'],
];

const childSafetyContactRows = [
  ['아동 안전 신고 및 담당 연락처', 'info@agitwidget.com'],
  ['접수 범위', 'CSAE/CSAM, 미성년자 대상 그루밍, 성적 대상화, 협박·갈취, 인신매매 또는 기타 아동 안전 위험'],
  ['긴급 위험', '아동이 현재 위험에 처한 경우 현지 수사기관 또는 아동 보호기관에 즉시 신고'],
];

function PolicyTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: Array<Array<ReactNode>>;
}) {
  return (
    <div className="policy-table-wrap">
      <table className="policy-table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const childSafetySections: PolicySection[] = [
  {
    id: 'principles',
    title: '1. 목적 및 원칙',
    content: (
      <>
        <p>
          할리는 Agit에서 아동 성적 학대 및 착취(Child Sexual Abuse and Exploitation,
          CSAE)와 아동 성적 학대 자료(Child Sexual Abuse Material, CSAM)를 명확히
          금지합니다. 회사는 아동의 안전을 해치는 콘텐츠와 행위에 대해 무관용 원칙을
          적용합니다.
        </p>
        <p>
          Agit은 만 14세 이상만 가입할 수 있는 서비스이나, 이용자가 사진·그림·텍스트 등
          콘텐츠를 만들고 공유할 수 있으므로 아동 안전 위험을 예방하고 신고에 대응하기 위한
          공개 표준을 운영합니다.
        </p>
      </>
    ),
  },
  {
    id: 'definitions',
    title: '2. CSAE 및 CSAM의 의미',
    content: (
      <ul>
        <li>CSAE는 아동을 대상으로 한 성적 학대, 착취, 유인, 협박, 거래, 성적 대상화 또는 이를 촉진하는 행위를 말합니다.</li>
        <li>CSAM은 실제 또는 가상의 아동 성적 학대·착취를 묘사하거나 이를 조장하는 이미지, 영상, 그림, 텍스트, 링크, 편집물, 합성물 또는 기타 자료를 말합니다.</li>
        <li>아동은 관련 법령상 미성년자 또는 보호가 필요한 사람을 포함하며, 이용자가 성인이라고 주장하더라도 아동으로 보이거나 아동을 묘사한 성적 콘텐츠는 금지됩니다.</li>
      </ul>
    ),
  },
  {
    id: 'prohibited-conduct',
    title: '3. 금지되는 행위와 콘텐츠',
    content: (
      <ul>
        <li>CSAM을 업로드, 생성, 저장, 공유, 요청, 홍보, 거래하거나 이에 접근할 수 있는 링크를 게시하는 행위</li>
        <li>아동 또는 아동으로 보이는 사람을 성적으로 묘사, 대상화, 평가, 유인하거나 성적 대화를 요구하는 행위</li>
        <li>미성년자를 대상으로 한 그루밍, 오프라인 만남 유도, 성적 이미지 교환 요청 또는 성적 접촉을 목적으로 한 접근</li>
        <li>아동의 사적 이미지나 개인정보를 이용한 협박, 갈취, 조롱, 괴롭힘 또는 유포 위협</li>
        <li>아동 성매매, 인신매매, 상업적 성착취, 착취 목적의 모집·광고·알선 또는 이를 암시하는 행위</li>
        <li>CSAE/CSAM 탐지, 신고, 조사 또는 조치를 회피하기 위해 콘텐츠를 변형하거나 은어·암호·외부 링크를 사용하는 행위</li>
      </ul>
    ),
  },
  {
    id: 'reporting',
    title: '4. 신고 방법',
    content: (
      <>
        <p>
          이용자는 이메일(
          <a href="mailto:info@agitwidget.com">info@agitwidget.com</a>)로 아동 안전 관련
          우려, 신고 또는 플랫폼 통지를 제출할 수 있습니다.
        </p>
        <PolicyTable headers={['구분', '내용']} rows={childSafetyContactRows} />
        <p>
          신고 시 가능한 경우 문제 콘텐츠가 표시된 그룹, 초대코드, 작성 시각, 관련 화면 또는
          설명을 함께 제공하면 검토에 도움이 됩니다. 단, CSAM으로 의심되는 자료를 직접
          다운로드하거나 재전송하지 마세요.
        </p>
      </>
    ),
  },
  {
    id: 'enforcement',
    title: '5. 검토 및 조치',
    content: (
      <ol>
        <li>회사는 신고, 플랫폼 통지, 자체 확인 또는 관계기관 요청을 통해 CSAE/CSAM 위험을 알게 된 경우 신속히 검토합니다.</li>
        <li>위반 가능성이 확인되면 콘텐츠 삭제, 노출 제한, 그룹 접근 제한, 계정 정지 또는 탈퇴 처리 등 필요한 조치를 할 수 있습니다.</li>
        <li>법령상 필요하거나 아동 안전 보호를 위해 필요한 경우 관련 기록을 보존하고 수사기관, 아동 보호기관, NCMEC 또는 관련 지역 기관에 신고·협조할 수 있습니다.</li>
        <li>긴급한 피해 방지 또는 법적 의무 이행을 위해 사전 통지 없이 조치할 수 있으며, 가능한 범위에서 사후 안내합니다.</li>
      </ol>
    ),
  },
  {
    id: 'legal-compliance',
    title: '6. 법령 및 플랫폼 정책 준수',
    content: (
      <>
        <p>
          회사는 아동 안전 관련 법령과 Google Play 등 앱마켓 정책을 준수하기 위해 신고 접수,
          검토, 삭제, 계정 제한, 관계기관 협조 절차를 운영합니다.
        </p>
        <p>
          아동이 현재 위험에 처했거나 학대·착취·인신매매 피해를 입었다고 판단되는 경우,
          Agit 신고와 별개로 즉시 현지 수사기관 또는 아동 보호기관에 신고해야 합니다.
        </p>
      </>
    ),
  },
  {
    id: 'contact',
    title: '7. 아동 안전 담당 연락처',
    content: (
      <>
        <p>
          Google Play, 앱마켓, 관계기관 및 이용자는 CSAE/CSAM 관련 통지와 조치 요청을 아래
          연락처로 보낼 수 있습니다.
        </p>
        <PolicyTable headers={['구분', '내용']} rows={operatorRows} />
        <p>
          아동 안전 신고 및 통지 담당 이메일:{' '}
          <a href="mailto:info@agitwidget.com">info@agitwidget.com</a>
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    title: '8. 표준의 변경',
    content: (
      <p>
        이 아동 안전 표준이 변경되는 경우 웹사이트 또는 서비스 내 공지를 통해 안내합니다.
        공고일 및 시행일은 2026년 6월 14일입니다.
      </p>
    ),
  },
];

export const privacySections: PolicySection[] = [
  {
    id: 'purpose',
    title: '1. 개인정보의 처리 목적',
    content: (
      <>
        <p>
          할리(이하 “회사”)는 Agit 애플리케이션과 웹사이트(이하 “서비스”)를 운영하기 위해
          필요한 범위에서 개인정보를 처리합니다. 수집한 개인정보는 아래 목적 외의 용도로
          이용하지 않으며, 목적이 변경되는 경우 관련 법령에 따라 별도 동의를 받는 등 필요한
          조치를 합니다.
        </p>
        <ul>
          <li>회원 가입, 본인 식별, 소셜 로그인 및 계정 관리</li>
          <li>프로필, 그룹, 초대코드와 공유 위젯 기능 제공</li>
          <li>사진·그림·텍스트의 저장, 변환, 동기화 및 그룹 구성원 간 표시</li>
          <li>푸시 알림, 홈 화면 위젯, Live Activity 등 기기 연동 기능 제공</li>
          <li>Agit Plus 이용 자격 확인, 사용량 관리, 결제 복원 및 고객지원</li>
          <li>오류 분석, 부정 이용 방지, 보안과 서비스 품질 개선</li>
          <li>이메일 문의 답변, 분쟁 처리 및 법적 의무 이행</li>
        </ul>
      </>
    ),
  },
  {
    id: 'items',
    title: '2. 처리하는 개인정보의 항목 및 보유기간',
    content: (
      <>
        <PolicyTable
          headers={['구분', '처리 항목', '목적', '보유기간']}
          rows={[
            [
              '소셜 로그인 및 계정',
              '회원 식별자, 로그인 제공자, 이메일, 제공자 프로필 이름, 인증 토큰 처리 결과',
              '가입, 로그인, 계정 관리',
              '회원 탈퇴 시까지',
            ],
            [
              '프로필',
              '닉네임, 프로필 이미지, 시간대',
              '프로필 표시, 그룹 구성원 식별',
              '회원 탈퇴 시까지',
            ],
            [
              '그룹 및 초대',
              '그룹명, 구성원·역할·가입 상태, 초대코드, 생성·가입·사용 기록',
              '그룹 생성, 초대, 구성원 관리',
              '그룹 삭제 또는 회원 탈퇴 시까지. 공유 관계 기록은 익명화 후 유지될 수 있음',
            ],
            [
              '공유 위젯 콘텐츠',
              '이용자가 올린 사진, 그림, 텍스트, 스티커, 배경, 편집 레이어, 렌더 이미지, 썸네일, 편집·게시 기록',
              '공동 편집, 위젯 게시, 보관함과 재생 기능',
              '이용자 또는 그룹이 삭제할 때까지. 탈퇴 후 공유본은 작성자 정보 제거 후 그룹 삭제 시까지',
            ],
            [
              '기기 및 알림',
              '기기 식별키, 플랫폼, 기기명·모델, OS·앱 버전, FCM·APNs 토큰, 위젯 종류, 최근 접속·활성 상태',
              '푸시 알림, 위젯 동기화, 중복 기기 관리',
              '회원 탈퇴, 기기 등록 해제 또는 토큰 만료 시까지',
            ],
            [
              '서비스 이용 및 오류 기록',
              '기능 이용량, 구독 자격·만료일·출처, 전송 상태, 오류 내용, 이벤트 시각, 토큰 일부',
              '이용 한도 적용, 장애 대응, 보안과 품질 개선',
              '목적 달성 시까지. 위젯 전송·디버그 기록은 생성 후 90일',
            ],
            [
              '이메일 고객문의',
              '이메일 주소, 문의 제목·내용, 메일 처리 기록',
              '문의 답변과 분쟁 대응',
              '처리 완료 후 3년',
            ],
            [
              'Agit Plus 결제 및 구독',
              '앱 내 사용자 ID, 상품·권한 식별자, 결제 상태, 구독 기간, 앱마켓 거래 식별자, Apple 영수증, Google purchase token, RevenueCat 고객 정보, RevenueCat SDK 진단 정보, 구매 복원 결과, 구독 갱신·해지·만료 이벤트, 결제 실패 또는 유예 상태와 웹훅 처리 결과',
              '구독 제공, 결제 복원, 환불과 분쟁 처리',
              '계약·결제 기록 5년, 소비자 불만·분쟁 기록 3년',
            ],
          ]}
        />
        <p>
          서비스 이용 과정에서 IP 주소, 접속 일시, 이용 기록, 단말 및 브라우저 정보가
          서비스 제공자의 시스템에 자동으로 생성될 수 있습니다. 회사는 웹사이트에 별도의
          광고 쿠키나 이용자 행동 분석 도구를 설치하지 않습니다.
        </p>
        <p>
          Agit Plus 결제는 App Store 또는 Google Play와 RevenueCat을 통해 처리됩니다.
          회사는 카드번호, 계좌번호, 전체 결제수단 인증정보 등 원 결제수단 정보를 직접
          수집하거나 저장하지 않습니다.
        </p>
      </>
    ),
  },
  {
    id: 'collection',
    title: '3. 개인정보의 수집 방법',
    content: (
      <ul>
        <li>Apple, Google, Kakao 계정을 이용한 회원 가입 및 로그인 과정</li>
        <li>프로필 작성, 그룹 참여, 위젯 편집, 이메일 문의 등 이용자의 직접 입력</li>
        <li>앱 권한을 통한 사진 선택, 알림 등록 및 기기 연동</li>
        <li>서비스 이용 과정에서 자동으로 생성되는 기록</li>
        <li>App Store 또는 Google Play가 제공하는 Agit Plus 결제 및 구독 결과</li>
        <li>RevenueCat이 앱마켓 영수증 검증 및 구독 상태 동기화 과정에서 제공하는 구매·권한 정보</li>
      </ul>
    ),
  },
  {
    id: 'permissions',
    title: '4. 선택적 접근 권한',
    content: (
      <>
        <PolicyTable
          headers={['권한', '이용 목적', '거부 시 영향']}
          rows={[
            ['사진', '프로필 및 공유 위젯 이미지 선택, 재생 영상 저장', '사진을 사용하는 기능이 제한됨'],
            ['알림', '위젯 변경, 초대 및 서비스 알림 수신', '푸시 알림을 받을 수 없음'],
          ]}
        />
        <p>
          선택 권한에 동의하지 않아도 해당 권한이 필요하지 않은 기능은 이용할 수 있습니다.
          권한은 기기 설정에서 언제든 변경할 수 있습니다.
        </p>
      </>
    ),
  },
  {
    id: 'third-parties',
    title: '5. 개인정보의 제3자 제공',
    content: (
      <>
        <p>
          회사는 원칙적으로 이용자의 개인정보를 외부에 제공하지 않습니다. 다만 이용자가
          사전에 동의한 경우, 법령에 특별한 규정이 있는 경우 또는 급박한 생명·신체 보호가
          필요한 경우 관련 법령이 허용하는 범위에서 제공할 수 있습니다.
        </p>
        <p>
          소셜 로그인 시 Apple, Google, Kakao와 각 사업자의 인증 화면에서 이용자가 선택한
          정보가 상호 전달될 수 있습니다. 이 과정은 이용자가 해당 로그인 수단을 선택한 경우에
          한해 이루어지며, 각 사업자의 개인정보 처리방침도 함께 적용됩니다.
        </p>
      </>
    ),
  },
  {
    id: 'processors',
    title: '6. 개인정보 처리업무의 위탁',
    content: (
      <>
        <p>
          회사는 안정적인 서비스 제공을 위해 다음 업무를 외부 사업자에게 위탁합니다. 위탁
          계약과 서비스 설정을 통해 개인정보가 목적 외로 처리되지 않도록 관리합니다.
        </p>
        <PolicyTable
          headers={['수탁자', '위탁 업무', '처리되는 정보', '보유기간']}
          rows={[
            [
              'Supabase, Inc.',
              '인증, 데이터베이스, 파일 저장, 서버 기능, 실시간 동기화',
              '계정·프로필·그룹·콘텐츠·기기·문의 등 서비스 데이터',
              '서비스 이용 또는 위탁 계약 종료 시까지',
            ],
            [
              'Google LLC (Firebase)',
              '푸시 알림 전송, Google 로그인, 웹 호스팅',
              '기기 토큰, 알림 데이터, 로그인 정보, 접속 기록',
              '서비스 이용 또는 위탁 계약 종료 시까지',
            ],
            [
              'Apple Inc.',
              'Apple 로그인, APNs 알림 및 앱마켓 결제',
              '로그인 정보, 기기 알림 토큰, 구독·결제 결과',
              '각 서비스 제공 목적 달성 시까지',
            ],
            [
              'RevenueCat, Inc.',
              'Agit Plus 구독 상태 확인, 결제 영수증 검증, 구매 복원, 구독 이벤트 웹훅 처리',
              '앱 내 사용자 ID, 기기·기술 정보, SDK 진단 정보, 상품·권한 식별자, Apple 영수증, Google purchase token, 구독 상태, 구매 복원 결과, 결제 실패 또는 유예 상태와 거래 이벤트',
              '서비스 이용 또는 위탁 계약 종료 시까지. 법령상 보관이 필요한 거래·분쟁 기록은 해당 기간까지',
            ],
            [
              'Kakao Corp.',
              'Kakao 로그인',
              '회원 식별자, 이메일, 닉네임, 인증 정보',
              '로그인 연동 해제 또는 회원 탈퇴 시까지',
            ],
            [
              'Replicate, Inc.',
              '이용자가 요청한 이미지 배경 제거',
              '처리 대상 이미지의 한시적 접근 주소와 결과 이미지',
              'API 입력·출력·로그는 생성 후 기본 1시간 이내 자동 삭제',
            ],
          ]}
        />
      </>
    ),
  },
  {
    id: 'overseas',
    title: '7. 개인정보의 국외 이전',
    content: (
      <>
        <p>
          서비스의 클라우드·알림·이미지 처리 기능을 이용하는 과정에서 개인정보가 아래와 같이
          국외로 이전될 수 있습니다. 이전은 네트워크를 통한 전송 방식으로 서비스 이용 시
          수시로 발생합니다.
        </p>
        <PolicyTable
          headers={['이전받는 자·국가', '목적', '이전 항목', '보유기간']}
          rows={[
            [
              'Supabase, Inc. / 싱가포르',
              '인증, 데이터베이스, 저장소와 서버 기능 운영',
              '서비스 제공에 필요한 계정·콘텐츠·기기·문의 정보',
              '회원 탈퇴 또는 위탁 계약 종료 시까지',
            ],
            [
              'Google LLC / 미국',
              'Google 로그인, Firebase 푸시·호스팅',
              '로그인 정보, 기기 토큰, 알림 데이터, 접속 기록',
              '목적 달성 또는 서비스 계약 종료 시까지',
            ],
            [
              'Apple Inc. / 미국',
              'Apple 로그인, APNs, 앱마켓 구독',
              '로그인 정보, 알림 토큰, 구독·결제 결과',
              '목적 달성 또는 연동 해제 시까지',
            ],
            [
              'RevenueCat, Inc. / 미국',
              'Agit Plus 구독 상태 확인, 영수증 검증, 구매 복원 및 구독 이벤트 처리',
              '앱 내 사용자 ID, 기기·기술 정보, SDK 진단 정보, 상품·권한 식별자, Apple 영수증, Google purchase token, 구독 상태, 구매 복원 결과, 결제 실패 또는 유예 상태와 거래 이벤트',
              '미국 AWS에 저장될 수 있으며, 서비스 이용 또는 위탁 계약 종료 시까지 보관. 법령상 보관이 필요한 거래·분쟁 기록은 해당 기간까지',
            ],
            [
              'Replicate, Inc. / 미국',
              '이용자가 요청한 이미지 배경 제거',
              '처리 대상 이미지의 한시적 접근 주소',
              'API 입력·출력·로그는 생성 후 기본 1시간 이내 자동 삭제',
            ],
          ]}
        />
        <p>
          국외 이전을 원하지 않는 경우 해당 기능을 사용하지 않거나 회원 탈퇴를 요청할 수
          있습니다. 다만 클라우드 저장 등 서비스 제공에 필수적인 이전을 거부하면 서비스 이용이
          어려울 수 있습니다.
        </p>
      </>
    ),
  },
  {
    id: 'destruction',
    title: '8. 개인정보의 파기 절차 및 방법',
    content: (
      <>
        <p>
          보유기간이 지나거나 처리 목적이 달성된 개인정보는 지체 없이 파기합니다. 전자적 파일은
          복구하기 어려운 방식으로 삭제하며, 종이 문서는 분쇄 또는 소각합니다.
        </p>
        <p>
          회원 탈퇴 시 계정, 프로필, 기기 토큰과 개인 저장 파일은 삭제됩니다. 여러 구성원이
          함께 만든 위젯과 그룹에 이미 공유된 콘텐츠는 공동 이용의 연속성을 위해 유지될 수
          있으며, 이 경우 탈퇴자의 닉네임·프로필 등 식별정보와 작성자 연결 정보를 제거합니다.
          익명화된 공유 콘텐츠는 이용자 또는 그룹이 삭제할 때까지 보관됩니다.
        </p>
      </>
    ),
  },
  {
    id: 'rights',
    title: '9. 이용자와 법정대리인의 권리 및 행사 방법',
    content: (
      <>
        <p>
          이용자는 자신의 개인정보에 대해 열람, 정정, 삭제, 처리정지, 동의 철회 및 회원 탈퇴를
          요구할 수 있습니다. 앱의 프로필·설정 화면, 웹 계정 삭제 페이지(
          <a href="https://agitwidget.com/delete-account">https://agitwidget.com/delete-account</a>)를 이용하거나
          {' '}
          <a href="mailto:info@agitwidget.com">info@agitwidget.com</a>
          으로 요청하면 본인 확인 후 관련 법령에서 정한 기간 내 처리합니다.
        </p>
        <p>
          Agit은 만 14세 이상만 가입할 수 있습니다. 회사가 만 14세 미만의 가입 사실을 확인한
          경우 계정 이용을 제한하고 관련 정보를 삭제할 수 있습니다.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: '10. 개인정보의 안전성 확보조치',
    content: (
      <ul>
        <li>업무 목적에 따른 접근 권한 제한과 인증 적용</li>
        <li>전송 구간 암호화 및 비공개 저장소 접근 제어</li>
        <li>접속·오류 기록 점검과 보안 업데이트</li>
        <li>개인정보 처리 서비스 제공자와 위탁 현황의 정기 점검</li>
      </ul>
    ),
  },
  {
    id: 'officer',
    title: '11. 개인정보 보호책임자 및 문의',
    content: (
      <>
        <PolicyTable headers={['구분', '내용']} rows={operatorRows} />
        <p>
          개인정보 관련 문의, 불만 처리, 피해 구제 또는 권리 행사는 위 연락처로 요청할 수
          있습니다.
        </p>
      </>
    ),
  },
  {
    id: 'remedies',
    title: '12. 권익침해 구제방법',
    content: (
      <ul>
        <li>개인정보침해 신고센터: 국번 없이 118, privacy.kisa.or.kr</li>
        <li>개인정보 분쟁조정위원회: 1833-6972, kopico.go.kr</li>
        <li>대검찰청: 국번 없이 1301, spo.go.kr</li>
        <li>경찰청: 국번 없이 182, ecrm.police.go.kr</li>
      </ul>
    ),
  },
  {
    id: 'changes',
    title: '13. 개인정보 처리방침의 변경',
    content: (
      <>
        <p>
          이 처리방침의 내용이 변경되는 경우 시행일 최소 7일 전 서비스 내 공지 또는 웹사이트를
          통해 안내합니다. 이용자 권리에 중대한 변경이 있는 경우에는 최소 30일 전에 안내합니다.
        </p>
        <p><strong>공고일 및 시행일: 2026년 6월 19일</strong></p>
      </>
    ),
  },
];

export const termsSections: PolicySection[] = [
  {
    id: 'article-1',
    title: '제1조 (목적)',
    content: (
      <p>
        이 약관은 할리(이하 “회사”)가 제공하는 Agit 애플리케이션 및 웹 관련 서비스의 이용
        조건과 절차, 회사와 이용자의 권리·의무 및 책임사항을 정함을 목적으로 합니다.
      </p>
    ),
  },
  {
    id: 'article-2',
    title: '제2조 (용어의 정의)',
    content: (
      <ol>
        <li>“서비스”란 그룹 구성원이 사진, 그림, 텍스트 등을 이용해 공유 위젯을 만들고 각자의 기기에 표시하는 Agit의 기능 일체를 말합니다.</li>
        <li>“이용자”란 이 약관에 동의하고 서비스를 이용하는 사람을 말합니다.</li>
        <li>“그룹” 또는 “Agit”은 초대코드를 통해 구성원이 참여하고 콘텐츠를 공동 편집하는 공간을 말합니다.</li>
        <li>“콘텐츠”란 이용자가 서비스에 입력·업로드·작성한 사진, 그림, 문구, 스티커, 위젯 구성과 편집 기록을 말합니다.</li>
        <li>“Agit Plus”란 회사가 월 단위로 제공하는 자동 갱신형 유료 구독 상품을 말합니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-3',
    title: '제3조 (약관의 게시와 변경)',
    content: (
      <ol>
        <li>회사는 이 약관을 서비스 또는 웹사이트에 게시합니다.</li>
        <li>회사는 관련 법령을 위반하지 않는 범위에서 약관을 변경할 수 있습니다.</li>
        <li>변경 약관은 시행일과 변경 사유를 명시해 원칙적으로 시행 7일 전 공지하며, 이용자에게 불리한 중대한 변경은 30일 전에 알립니다.</li>
        <li>이용자가 변경 약관에 동의하지 않으면 서비스 이용을 중단하고 탈퇴할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-4',
    title: '제4조 (운영자 정보)',
    content: <PolicyTable headers={['구분', '내용']} rows={operatorRows} />,
  },
  {
    id: 'article-5',
    title: '제5조 (가입 및 이용 연령)',
    content: (
      <ol>
        <li>이용계약은 이용자가 소셜 로그인 후 약관과 개인정보 처리에 동의하고 회사가 가입을 승인함으로써 성립합니다.</li>
        <li>서비스는 만 14세 이상만 이용할 수 있습니다.</li>
        <li>이용자는 사실에 부합하는 정보를 제공해야 하며 계정과 인증 수단을 안전하게 관리해야 합니다.</li>
        <li>타인의 정보를 사용하거나 서비스 운영을 방해할 우려가 있는 경우 회사는 가입을 거절하거나 이용을 제한할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-6',
    title: '제6조 (서비스의 내용)',
    content: (
      <ul>
        <li>프로필, 그룹 생성·참여 및 초대코드 관리</li>
        <li>사진, 그림, 텍스트, 스티커 등을 이용한 공유 위젯 공동 편집</li>
        <li>홈 화면 위젯, 잠금 화면, Live Activity와 푸시 알림 연동</li>
        <li>위젯 보관함, 복제, 재생 및 이미지 배경 제거</li>
        <li>Agit Plus 등 회사가 추가로 제공하는 기능</li>
      </ul>
    ),
  },
  {
    id: 'article-7',
    title: '제7조 (그룹 및 초대코드)',
    content: (
      <ol>
        <li>초대코드를 가진 사람은 코드의 유효기간과 이용 한도 내에서 그룹에 참여할 수 있습니다.</li>
        <li>이용자는 신뢰할 수 있는 사람에게만 초대코드를 전달해야 하며, 공개된 코드로 발생한 가입에 대해 회사의 귀책사유가 없는 한 회사는 책임을 지지 않습니다.</li>
        <li>그룹 소유자가 탈퇴하거나 그룹을 삭제하는 경우 회사는 다른 구성원에게 소유자 역할을 이전하거나 그룹 이용을 종료할 수 있습니다.</li>
        <li>그룹에서 나가더라도 이미 공동 편집된 콘텐츠는 작성자 정보가 제거된 상태로 그룹에 남을 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-8',
    title: '제8조 (이용자의 의무)',
    content: (
      <p>이용자는 관련 법령, 이 약관, 서비스 안내와 다른 이용자의 권리를 준수해야 하며 다음 행위를 해서는 안 됩니다.</p>
    ),
  },
  {
    id: 'article-9',
    title: '제9조 (금지행위)',
    content: (
      <ul>
        <li>타인의 계정, 개인정보, 초상, 저작물 또는 권리를 무단으로 이용하는 행위</li>
        <li>불법·음란·폭력·혐오·차별·괴롭힘·스토킹·사칭 콘텐츠를 게시하는 행위</li>
        <li>
          CSAE/CSAM 또는{' '}
          <a href="https://agitwidget.com/child-safety">아동 안전 표준</a>
          을 위반하는 콘텐츠를 게시하거나 아동을 위험에 빠뜨리는 행위
        </li>
        <li>악성코드, 자동화 수단, 비정상 요청 등으로 서비스나 다른 이용을 방해하는 행위</li>
        <li>서비스를 역설계하거나 접근 제한을 우회하고 데이터를 무단 수집하는 행위</li>
        <li>초대코드, 계정 또는 유료 기능을 판매·대여·양도하는 행위</li>
        <li>기타 법령 또는 공공질서에 반하거나 회사가 합리적으로 부적절하다고 판단하는 행위</li>
      </ul>
    ),
  },
  {
    id: 'article-10',
    title: '제10조 (콘텐츠의 권리와 이용허락)',
    content: (
      <ol>
        <li>이용자가 작성한 콘텐츠의 권리는 해당 이용자 또는 정당한 권리자에게 있습니다.</li>
        <li>이용자는 자신이 콘텐츠를 서비스에 게시할 권리를 보유하며 제3자의 권리를 침해하지 않음을 보증합니다.</li>
        <li>이용자는 서비스 제공에 필요한 범위에서 회사가 콘텐츠를 저장, 복제, 변환, 전송하고 해당 그룹 구성원의 기기에 표시할 수 있도록 비독점적 이용권을 부여합니다.</li>
        <li>이 이용권은 서비스 제공과 백업·복구에 필요한 기간 동안만 유효하며, 법령상 보관 의무와 익명화된 공동 콘텐츠를 제외하고 삭제 시 종료됩니다.</li>
        <li>회사는 이용자의 별도 동의 없이 콘텐츠를 광고 소재로 사용하지 않습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-11',
    title: '제11조 (콘텐츠 관리와 신고)',
    content: (
      <ol>
        <li>회사는 신고를 받거나 위법·권리침해·유해 가능성이 확인된 콘텐츠의 노출을 제한하거나 삭제할 수 있습니다.</li>
        <li>
          CSAE/CSAM 또는 아동 안전 위험 신고는{' '}
          <a href="https://agitwidget.com/child-safety">아동 안전 표준</a>
          에 따라 우선 검토하고 필요한 경우 관련 기관에 신고·협조할 수 있습니다.
        </li>
        <li>긴급한 피해 방지를 위해 사전 통지 없이 조치할 수 있으며, 가능한 경우 사후에 사유를 알립니다.</li>
        <li>권리침해 신고와 조치에 대한 이의는 info@agitwidget.com으로 제출할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-12',
    title: '제12조 (Agit Plus 및 인앱 구독)',
    content: (
      <ol>
        <li>Agit Plus는 App Store 또는 Google Play의 인앱 결제를 통해 월 ₩1,900의 월간 자동 갱신 구독으로 제공됩니다.</li>
        <li>회사는 RevenueCat을 통해 앱마켓 구매 영수증과 구독 상태를 확인하고, 확인된 구독 권한을 서비스 계정에 반영합니다.</li>
        <li>Agit Plus 이용자는 무료 플랜의 아지트 수, 구성원 수, 보관함, 배경 제거, 위젯 발행 횟수 등 일부 제한이 완화되거나 해제된 기능을 이용할 수 있습니다. 구체적인 제공 범위는 앱 내 구매 화면과 기능 안내에 표시합니다.</li>
        <li>상품 식별자는 agit_plus_monthly이며, 최종 가격, 세금, 제공 기능과 결제 조건은 이용자가 결제를 확정하기 전에 앱마켓 구매 화면에 표시된 내용을 기준으로 합니다.</li>
        <li>무료 체험 대상인 이용자에게는 7일의 무료 체험이 제공됩니다. 무료 체험 가능 여부는 앱마켓 계정의 이용 이력과 구매 화면 표시를 기준으로 합니다.</li>
        <li>무료 체험 종료 최소 24시간 전까지 구독을 해지하지 않으면 구매 화면에 표시된 월간 구독 가격으로 자동 결제됩니다.</li>
        <li>구독은 다음 결제일 24시간 전까지 앱마켓 계정 설정에서 해지하지 않으면 매월 자동 갱신됩니다.</li>
        <li>구독 취소는 앱 내 Agit Plus 화면의 구독 취소 메뉴 또는 App Store·Google Play 구독 관리 화면에서 진행할 수 있습니다.</li>
        <li>구독을 취소하더라도 이미 결제된 구독 기간이 끝날 때까지 Agit Plus 기능을 이용할 수 있습니다. 다만 앱마켓 정책이나 환불 처리 결과에 따라 이용 가능 기간이 달라질 수 있습니다.</li>
        <li>Google Play 결제 실패 시 Google Play 정책에 따라 결제 재시도, 유예 기간, 계정 보류 또는 자동 취소가 발생할 수 있으며, 이 기간의 서비스 이용 가능 여부는 앱마켓의 구독 상태와 회사가 확인한 구독 권한을 기준으로 합니다.</li>
        <li>앱 삭제, 로그아웃 또는 회원 탈퇴만으로 앱마켓 구독이 자동 해지되지 않으므로 이용자는 해당 앱마켓에서 구독 상태를 확인하고 필요한 경우 별도로 해지해야 합니다.</li>
        <li>이용자는 동일한 앱마켓 계정을 사용하는 기기에서 회사가 제공하는 구매 복원 기능을 통해 구독 이용 권한을 복원할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-13',
    title: '제13조 (청약철회, 해지 및 환불)',
    content: (
      <ol>
        <li>인앱 결제의 취소와 환불은 원칙적으로 결제를 처리한 App Store 또는 Google Play의 절차와 정책에 따릅니다.</li>
        <li>디지털 콘텐츠가 제공되기 시작한 경우 관련 법령에 따라 청약철회가 제한될 수 있으며, 제한되는 경우 구매 화면에서 이를 알립니다.</li>
        <li>서비스 장애나 회사의 귀책사유로 유료 기능을 정상적으로 이용하지 못한 경우 관련 법령과 앱마켓 정책에 따라 환불, 이용기간 연장 또는 이에 상응하는 보상을 제공합니다.</li>
        <li>이 조항은 관련 법령이 보장하는 소비자의 권리를 제한하지 않습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-14',
    title: '제14조 (서비스의 변경 및 중단)',
    content: (
      <ol>
        <li>회사는 운영·기술상 필요에 따라 서비스의 전부 또는 일부를 변경할 수 있습니다.</li>
        <li>점검, 장애, 통신망 문제, 천재지변, 외부 플랫폼 정책 변경 등 불가피한 사유가 있는 경우 서비스를 일시 중단할 수 있습니다.</li>
        <li>예측 가능한 중대한 변경이나 중단은 사전에 알리며, 긴급한 경우 사후에 안내할 수 있습니다.</li>
        <li>유료 서비스의 중대한 축소·종료 시 관련 법령에 따른 환불 또는 대체 조치를 제공합니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-15',
    title: '제15조 (이용 제한)',
    content: (
      <ol>
        <li>이용자가 법령이나 약관을 위반한 경우 회사는 경고, 콘텐츠 제한, 기능 정지, 계정 정지 또는 계약 해지 조치를 할 수 있습니다.</li>
        <li>회사는 원칙적으로 조치 사유와 이의제기 방법을 알리며, 보안 또는 다른 이용자의 피해 방지를 위해 긴급한 경우 사후 통지할 수 있습니다.</li>
        <li>이용자는 통지를 받은 날부터 30일 이내에 info@agitwidget.com으로 이의를 제기할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-16',
    title: '제16조 (회원 탈퇴)',
    content: (
      <ol>
        <li>
          이용자는 앱 설정 또는 웹 계정 삭제 페이지(
          <a href="https://agitwidget.com/delete-account">https://agitwidget.com/delete-account</a>)에서 언제든 회원 탈퇴를 요청할 수 있습니다.
        </li>
        <li>탈퇴하면 계정, 프로필, 개인 설정과 비공유 저장 파일은 삭제되어 복구할 수 없습니다.</li>
        <li>그룹에 이미 공유되어 여러 이용자가 공동 편집한 콘텐츠는 서비스 연속성을 위해 작성자 정보가 제거된 상태로 남을 수 있습니다.</li>
        <li>진행 중인 앱마켓 구독은 이용자가 해당 앱마켓에서 별도로 해지해야 할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-17',
    title: '제17조 (책임 제한)',
    content: (
      <ol>
        <li>회사는 고의 또는 과실 없이 발생한 천재지변, 통신 장애, 외부 플랫폼 장애 등 통제하기 어려운 사유로 서비스를 제공하지 못한 경우 책임을 지지 않습니다.</li>
        <li>회사는 이용자의 귀책사유, 기기 설정, 초대코드 관리 또는 이용자 간 분쟁으로 발생한 손해에 대해 회사의 고의·과실이 없는 한 책임을 지지 않습니다.</li>
        <li>회사의 손해배상 책임을 법률상 허용되지 않는 방식으로 배제하거나 소비자에게 부당하게 전가하지 않습니다.</li>
      </ol>
    ),
  },
  {
    id: 'article-18',
    title: '제18조 (손해배상)',
    content: (
      <p>
        회사 또는 이용자가 자신의 책임 있는 사유로 상대방에게 손해를 입힌 경우 관련 법령에
        따라 그 손해를 배상합니다. 이용자가 제3자의 권리를 침해하여 회사에 손해가 발생한 경우
        이용자는 자신의 책임 범위에서 이를 배상해야 합니다.
      </p>
    ),
  },
  {
    id: 'article-19',
    title: '제19조 (통지)',
    content: (
      <p>
        회사는 앱 내 공지, 푸시 알림, 가입 이메일 또는 웹사이트를 통해 이용자에게 통지할 수
        있습니다. 전체 이용자에게 적용되는 사항은 서비스 내 7일 이상 게시하는 방법으로 개별
        통지를 대신할 수 있습니다. 다만 이용자에게 중대한 영향을 주는 사항은 가능한 범위에서
        개별 통지합니다.
      </p>
    ),
  },
  {
    id: 'article-20',
    title: '제20조 (준거법 및 분쟁 해결)',
    content: (
      <ol>
        <li>이 약관과 서비스 이용에 관한 사항은 대한민국 법률을 적용합니다.</li>
        <li>회사와 이용자는 분쟁이 발생한 경우 원만한 해결을 위해 성실히 협의합니다.</li>
        <li>협의로 해결되지 않는 분쟁은 민사소송법 등 관련 법령에 따른 관할 법원에 제기할 수 있습니다.</li>
      </ol>
    ),
  },
  {
    id: 'supplement',
    title: '부칙',
    content: <p><strong>이 약관은 2026년 6월 19일부터 시행합니다.</strong></p>,
  },
];
